/**
 * useImageIteration hook - Manages image iteration cycle (max 3 attempts).
 *
 * Provides:
 * - Iteration tracking
 * - Image history
 * - Refinement workflow
 * - Accept/reject flow
 */

import { useState, useCallback, useMemo } from 'react'
import { createApiClient } from '@/services/api'
import type { GeneratedImage } from '@/models'

/**
 * Iteration status
 */
export type IterationStatus =
  | 'idle'
  | 'generating'
  | 'ready'
  | 'refining'
  | 'accepted'
  | 'max_reached'

/**
 * Image iteration state
 */
export interface ImageIterationState {
  status: IterationStatus
  currentImage: GeneratedImage | null
  imageHistory: GeneratedImage[]
  currentIteration: number
  maxIterations: number
  canRefine: boolean
  isLoading: boolean
  error: string | null
}

/**
 * Image iteration actions
 */
export interface ImageIterationActions {
  generateInitial: () => Promise<GeneratedImage | null>
  refine: (feedback: string) => Promise<GeneratedImage | null>
  accept: () => void
  selectImage: (index: number) => void
  reset: () => void
}

/**
 * useImageIteration hook return type
 */
export interface UseImageIterationReturn extends ImageIterationState, ImageIterationActions {}

/**
 * Maximum number of iterations allowed
 */
const MAX_ITERATIONS = 3

/**
 * Hook for managing image iteration workflow
 */
export function useImageIteration(sessionId: string | null): UseImageIterationReturn {
  const [status, setStatus] = useState<IterationStatus>('idle')
  const [imageHistory, setImageHistory] = useState<GeneratedImage[]>([])
  const [currentImageIndex, setCurrentImageIndex] = useState(-1)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const apiClient = useMemo(() => createApiClient(), [])

  // Derived values
  const currentIteration = imageHistory.length
  const canRefine = currentIteration < MAX_ITERATIONS && status === 'ready'
  const currentImage: GeneratedImage | null = currentImageIndex >= 0 ? (imageHistory[currentImageIndex] ?? null) : null

  /**
   * Generate initial image from conversation themes
   */
  const generateInitial = useCallback(async (): Promise<GeneratedImage | null> => {
    if (!sessionId) {
      setError('No active session')
      return null
    }

    setIsLoading(true)
    setError(null)
    setStatus('generating')

    try {
      // Initial generation uses conversation themes (no feedback needed)
      const result = await apiClient.requestImageIteration(sessionId, {
        refinement_feedback: '',
      })

      // Add to history
      setImageHistory([result])
      setCurrentImageIndex(0)
      setStatus('ready')
      setIsLoading(false)

      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate image'
      setError(errorMessage)
      setStatus('idle')
      setIsLoading(false)
      return null
    }
  }, [sessionId, apiClient])

  /**
   * Refine current image with feedback
   */
  const refine = useCallback(
    async (feedback: string): Promise<GeneratedImage | null> => {
      if (!sessionId) {
        setError('No active session')
        return null
      }

      if (!canRefine) {
        setError('Maximum iterations reached')
        return null
      }

      if (!feedback.trim()) {
        setError('Please provide feedback for refinement')
        return null
      }

      setIsLoading(true)
      setError(null)
      setStatus('refining')

      try {
        const result = await apiClient.requestImageIteration(sessionId, {
          refinement_feedback: feedback.trim(),
        })

        // Add to history
        setImageHistory((prev) => [...prev, result])
        setCurrentImageIndex((prev) => prev + 1)

        // Check if max iterations reached
        if (imageHistory.length + 1 >= MAX_ITERATIONS) {
          setStatus('max_reached')
        } else {
          setStatus('ready')
        }

        setIsLoading(false)
        return result
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to refine image'
        setError(errorMessage)
        setStatus('ready')
        setIsLoading(false)
        return null
      }
    },
    [sessionId, apiClient, canRefine, imageHistory.length]
  )

  /**
   * Accept current image
   */
  const accept = useCallback(() => {
    if (currentImage) {
      setStatus('accepted')
    }
  }, [currentImage])

  /**
   * Select an image from history
   */
  const selectImage = useCallback(
    (index: number) => {
      if (index >= 0 && index < imageHistory.length) {
        setCurrentImageIndex(index)
      }
    },
    [imageHistory.length]
  )

  /**
   * Reset iteration state
   */
  const reset = useCallback(() => {
    setStatus('idle')
    setImageHistory([])
    setCurrentImageIndex(-1)
    setIsLoading(false)
    setError(null)
  }, [])

  return {
    status,
    currentImage,
    imageHistory,
    currentIteration,
    maxIterations: MAX_ITERATIONS,
    canRefine,
    isLoading,
    error,
    generateInitial,
    refine,
    accept,
    selectImage,
    reset,
  }
}

export default useImageIteration
